import React, { useState } from "react";
import "./Policy.css";

interface TocItem {
  id: string;
  label: string;
}

const toc: TocItem[] = [
  { id: "who-we-are", label: "1. Who We Are" },
  { id: "what-is-personal-data", label: "2. What Is Personal Data?" },
  { id: "how-we-collect", label: "3. How We Collect Personal Data" },
  { id: "purposes-lawful-bases", label: "4. Purposes of Processing and Lawful Bases" },
  { id: "specific-processing", label: "5. Specific Processing Activities" },
  { id: "recruitment", label: "6. Recruitment and Candidate Information" },
  { id: "employee-data", label: "7. Employee Personal Data" },
  { id: "employee-file-retention", label: "8. Employee Personal File Retention" },
  { id: "employee-media", label: "9. Employee Photographs, Videos and Marketing Information" },
  { id: "marketing", label: "10. Marketing and Business Communications" },
  { id: "website-forms", label: "11. Website Forms and Enquiries" },
  { id: "cookies", label: "12. Cookies and Similar Technologies" },
  { id: "security", label: "13. Information Security" },
  { id: "who-we-share", label: "14. Who We Share Personal Data With" },
  { id: "subprocessors", label: "15. Third-Party Service Providers and Subprocessors" },
  { id: "client-data", label: "16. Client Data and Outsourced Services" },
  { id: "international-transfers", label: "17. International Data Transfers" },
  { id: "data-retention", label: "18. Data Retention" },
  { id: "your-rights", label: "19. Your Data Protection Rights" },
  { id: "exercise-rights", label: "20. How to Exercise Your Data Protection Rights" },
  { id: "withdrawal-of-consent", label: "21. Withdrawal of Consent" },
  { id: "complaints", label: "22. Complaints" },
  { id: "childrens-privacy", label: "23. Children's Privacy" },
  { id: "special-categories", label: "24. Special Categories of Personal Data" },
  { id: "automated-decisions", label: "25. Automated Decision-Making and Profiling" },
  { id: "third-party-sites", label: "26. Third-Party Websites" },
  { id: "data-breaches", label: "27. Data Breaches and Security Incidents" },
  { id: "confidentiality", label: "28. Confidentiality" },
  { id: "changes", label: "29. Changes to This Privacy Policy" },
  { id: "contact", label: "30. Contact Us" },
];

const Policy: React.FC = () => {
  const [tocOpen, setTocOpen] = useState(false);

  return (
    <section className="policy-container">
      <div className="policy-content">
        <header className="policy-header">
          <h1 className="policy-title">Privacy Policy</h1>
          <p className="policy-date">
            Effective Date: 25 August 2026 &nbsp;•&nbsp; Last Updated: 25 August 2026
          </p>
          <p className="policy-intro">
            Tellida ("Tellida", "we", "our", or "us") is committed to protecting the
            privacy, confidentiality and security of personal information entrusted to
            us. Tellida provides business process outsourcing, staff augmentation,
            conveyancing support, contact centre services, finance and accounting
            outsourcing, and other professional outsourcing services to clients in the
            United Kingdom and other jurisdictions.
          </p>
          <p className="policy-paragraph">This Privacy Policy explains how we collect, use, disclose, retain and protect personal data when you:</p>
          <ul className="policy-list">
            <li>Visit or use our website;</li>
            <li>Contact us through our website, email, telephone or other communication channels;</li>
            <li>Enquire about our services;</li>
            <li>Engage with us as a client, prospective client, supplier or business partner;</li>
            <li>Apply for employment or submit a CV to us;</li>
            <li>Work for or with Tellida;</li>
            <li>Participate in Tellida events, marketing activities or corporate communications; or</li>
            <li>Otherwise interact with Tellida in a professional or business capacity.</li>
          </ul>
          <p className="policy-paragraph">
            We process personal data in accordance with applicable data protection and
            privacy laws and regulations, including applicable requirements under Sri
            Lankan data protection legislation and, where applicable, UK data protection
            legislation.
          </p>
          <p className="policy-paragraph">
            Tellida also maintains an Information Security Management System (ISMS)
            aligned with ISO/IEC&nbsp;27001:2022, supporting the security and protection
            of information processed by the organisation.
          </p>
          <p className="policy-paragraph">
            This Privacy Policy is a general public-facing privacy notice. Where a
            particular processing activity is subject to additional privacy
            information, consent requirements, contractual terms, client instructions
            or a separate notice, those requirements will also apply.
          </p>
        </header>

        {/* Table of contents */}
        <nav className="policy-toc" aria-label="Table of contents">
          <button
            type="button"
            className="policy-toc-toggle"
            onClick={() => setTocOpen((o) => !o)}
            aria-expanded={tocOpen}
          >
            <span>On this page</span>
            <span className={`policy-toc-caret${tocOpen ? " open" : ""}`}>▾</span>
          </button>
          <ul className={`policy-toc-list${tocOpen ? " open" : ""}`}>
            {toc.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} onClick={() => setTocOpen(false)}>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="policy-section" id="who-we-are">
          <h2 className="policy-heading">1. Who We Are</h2>
          <p className="policy-paragraph">
            The relevant Tellida entity responsible for processing your personal data
            will depend on the nature of your relationship with us and the services
            involved.
          </p>
          <p className="policy-paragraph">
            <strong>Tellida (Pvt) Ltd</strong>
            <br />
            Registered Office: Level 05, No. 228, Galle Road, Colombo 04, Sri Lanka
          </p>
          <p className="policy-paragraph">
            <strong>Tellida Limited</strong>
            <br />
            Tellida also operates through its UK entity, Tellida Limited, where
            applicable to our UK operations and contractual relationships.
          </p>
          <p className="policy-paragraph">
            Where Tellida acts as a data controller, we determine the purposes and
            means of processing personal data.
          </p>
          <p className="policy-paragraph">
            Where Tellida provides outsourced services to a client and processes
            personal data on that client's behalf, Tellida may act as a data processor
            or service provider. In those circumstances, the relevant client may remain
            the data controller and Tellida will process personal data in accordance
            with the client's documented instructions, contractual requirements and
            applicable law.
          </p>
          <p className="policy-paragraph">
            Where Tellida acts as a processor, this Privacy Policy does not replace the
            privacy notice or instructions of the relevant client who acts as the
            controller.
          </p>
        </div>

        <div className="policy-section" id="what-is-personal-data">
          <h2 className="policy-heading">2. What Is Personal Data?</h2>
          <p className="policy-paragraph">
            Personal data means information relating to an identified or identifiable
            individual. Depending on how you interact with Tellida, this may include:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Name and surname</li>
            <li>Job title and professional designation</li>
            <li>Company or organisation name</li>
            <li>Business email address</li>
            <li>Personal email address where voluntarily provided</li>
            <li>Telephone or mobile number</li>
            <li>Business contact details</li>
            <li>Postal or residential address where required</li>
            <li>Date of birth where required for legitimate business, employment or regulatory purposes</li>
            <li>National identification or other identification information where legally required</li>
            <li>CV and employment history</li>
            <li>Education, qualifications and professional experience</li>
            <li>Employment and HR information</li>
            <li>Photographs and video recordings</li>
            <li>Information contained in communications with Tellida</li>
            <li>Information submitted through forms on our website</li>
            <li>Information relating to recruitment and employment</li>
            <li>Information required for payroll, benefits or statutory administration</li>
            <li>Bank or payment information where required for employment or financial administration</li>
            <li>Website usage and technical information</li>
            <li>IP address</li>
            <li>Browser type and device information</li>
            <li>Operating system</li>
            <li>Cookies and similar technologies</li>
            <li>Other information that you voluntarily provide to us or that we are legally permitted to obtain</li>
          </ul>
          <p className="policy-paragraph">
            We only seek to collect personal data that is relevant and reasonably
            necessary for the relevant purpose.
          </p>
        </div>

        <div className="policy-section" id="how-we-collect">
          <h2 className="policy-heading">3. How We Collect Personal Data</h2>
          <p className="policy-paragraph">We may collect personal data through the following sources.</p>

          <h3 className="policy-subheading">3.1 Directly from you</h3>
          <p className="policy-paragraph">For example, when you:</p>
          <ul className="policy-list">
            <li>Complete a website contact form;</li>
            <li>Request information about our services;</li>
            <li>Contact us by email or telephone;</li>
            <li>Submit a CV or job application;</li>
            <li>Communicate with our employees;</li>
            <li>Attend a Tellida event;</li>
            <li>Subscribe to marketing communications; or</li>
            <li>Provide information as part of a business relationship.</li>
          </ul>

          <h3 className="policy-subheading">3.2 From your organisation</h3>
          <p className="policy-paragraph">
            Where you are acting on behalf of a company, law firm, financial
            institution, supplier or other organisation, we may receive your
            professional contact information from that organisation.
          </p>

          <h3 className="policy-subheading">3.3 From publicly available sources</h3>
          <p className="policy-paragraph">
            We may obtain limited professional information from publicly available
            business sources, professional networking platforms, company websites,
            business directories or other legitimate sources for business development,
            relationship management and research purposes. Where applicable, such
            processing will be carried out in accordance with relevant data protection
            requirements.
          </p>

          <h3 className="policy-subheading">3.4 Automatically through our website</h3>
          <p className="policy-paragraph">
            When you visit our website, certain technical information may be collected
            automatically through cookies, analytics tools and similar technologies.
          </p>
        </div>

        <div className="policy-section" id="purposes-lawful-bases">
          <h2 className="policy-heading">4. Purposes of Processing and Lawful Bases</h2>
          <p className="policy-paragraph">
            Where applicable data protection law requires us to identify a lawful basis
            for processing personal data, Tellida will identify the relevant lawful
            basis for the particular processing activity. The following table explains
            the principal processing activities carried out by Tellida and the lawful
            bases that may apply.
          </p>
          <div className="policy-table-wrap">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>Processing Activity</th>
                  <th>Personal Data Typically Used</th>
                  <th>Primary Lawful Basis, Where Applicable</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Website contact forms and general enquiries</td>
                  <td>Name, email, telephone number, company, enquiry details</td>
                  <td>Legitimate interests and/or steps taken at the individual's request before entering into a contract</td>
                </tr>
                <tr>
                  <td>Responding to service enquiries</td>
                  <td>Name, professional contact details, company information, communications</td>
                  <td>Legitimate interests and/or steps taken at the individual's request before entering into a contract</td>
                </tr>
                <tr>
                  <td>Client and prospective-client relationship management</td>
                  <td>Name, job title, company, professional contact details, communications</td>
                  <td>Legitimate interests and/or contract</td>
                </tr>
                <tr>
                  <td>Provision of contracted services</td>
                  <td>Contact details, business information and information necessary to deliver the service</td>
                  <td>Contract and/or legitimate interests</td>
                </tr>
                <tr>
                  <td>Supplier and business-partner management</td>
                  <td>Name, job title, contact details, contractual and business information</td>
                  <td>Contract and/or legitimate interests</td>
                </tr>
                <tr>
                  <td>Recruitment and candidate assessment</td>
                  <td>Name, contact details, CV, employment history, qualifications, interview and assessment information</td>
                  <td>Legitimate interests and/or steps taken at the individual's request before entering into a contract; consent where specifically required</td>
                </tr>
                <tr>
                  <td>Recruitment retention for future opportunities</td>
                  <td>CV, qualifications, employment history and contact details</td>
                  <td>Consent where required for continued consideration beyond the applicable recruitment purpose or retention period</td>
                </tr>
                <tr>
                  <td>Employee administration</td>
                  <td>Identification, contact, employment, attendance, performance and HR information</td>
                  <td>Contract and/or legal obligation</td>
                </tr>
                <tr>
                  <td>Payroll and statutory administration</td>
                  <td>Identification, employment, banking and statutory information</td>
                  <td>Contract and/or legal obligation</td>
                </tr>
                <tr>
                  <td>Employee photographs and videos for corporate communications</td>
                  <td>Name, image, job title and recordings</td>
                  <td>Consent where required by applicable law</td>
                </tr>
                <tr>
                  <td>Business development and professional communications</td>
                  <td>Name, job title, company, business contact details and professional interactions</td>
                  <td>Legitimate interests and/or consent where required</td>
                </tr>
                <tr>
                  <td>Direct marketing</td>
                  <td>Business or personal contact details and communication preferences</td>
                  <td>Consent and/or legitimate interests where permitted by applicable law</td>
                </tr>
                <tr>
                  <td>Website analytics</td>
                  <td>IP address, device information, browser information, usage information and cookie identifiers</td>
                  <td>Consent where required by applicable law; otherwise another lawful basis where legally permitted</td>
                </tr>
                <tr>
                  <td>Essential website operation and security</td>
                  <td>IP address, device information, technical information and security logs</td>
                  <td>Legitimate interests and/or legal obligation where applicable</td>
                </tr>
                <tr>
                  <td>Fraud prevention and information security</td>
                  <td>Technical information, access records, communications and security logs</td>
                  <td>Legitimate interests and/or legal obligation</td>
                </tr>
                <tr>
                  <td>Legal claims and dispute management</td>
                  <td>Relevant communications, records and identifying information</td>
                  <td>Legitimate interests and/or legal obligation</td>
                </tr>
                <tr>
                  <td>Regulatory and statutory compliance</td>
                  <td>Relevant personal and business information</td>
                  <td>Legal obligation</td>
                </tr>
                <tr>
                  <td>Client-directed outsourced processing</td>
                  <td>Personal data specified by the relevant client</td>
                  <td>Processing on behalf of the client under contract and documented instructions</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="policy-paragraph">
            The lawful basis applicable to a particular processing activity will depend
            on the circumstances, the relationship between Tellida and the individual
            or organisation, the nature of the information being processed and the law
            applicable to that processing.
          </p>
          <p className="policy-paragraph">
            Where Tellida relies on legitimate interests, we will consider whether the
            processing is necessary for the relevant legitimate interest and whether
            the individual's rights and interests override those interests.
          </p>
          <p className="policy-paragraph">
            Where Tellida relies on consent, the consent will relate to a specific
            processing purpose and may be withdrawn at any time.
          </p>
          <p className="policy-paragraph">
            Where processing is necessary to comply with a legal obligation, withdrawal
            of consent or objection may not prevent Tellida from continuing to process
            information where the law requires us to do so.
          </p>
        </div>

        <div className="policy-section" id="specific-processing">
          <h2 className="policy-heading">5. Specific Processing Activities</h2>
          <p className="policy-paragraph">
            Tellida may use personal data for the following purposes, depending on the
            individual's relationship with us:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Responding to enquiries</li>
            <li>Providing information about Tellida and our services</li>
            <li>Managing client and prospective-client relationships</li>
            <li>Delivering contracted services</li>
            <li>Managing suppliers and business partners</li>
            <li>Recruitment and candidate assessment</li>
            <li>Managing employment relationships</li>
            <li>HR administration</li>
            <li>Payroll and statutory administration</li>
            <li>Maintaining company and employee records</li>
            <li>Managing events and corporate activities</li>
            <li>Business development and professional communications</li>
            <li>Sending permitted marketing communications</li>
            <li>Improving our website and services</li>
            <li>Website administration and security</li>
            <li>Detecting and preventing fraud, misuse or security incidents</li>
            <li>Maintaining records and audit trails</li>
            <li>Complying with legal and regulatory obligations</li>
            <li>Exercising or defending legal rights</li>
            <li>Protecting the rights, property and security of Tellida, our clients, employees and other stakeholders</li>
          </ul>
          <p className="policy-paragraph">
            We will not use personal data for purposes that are incompatible with the
            purpose for which it was collected unless permitted or required by
            applicable law.
          </p>
        </div>

        <div className="policy-section" id="recruitment">
          <h2 className="policy-heading">6. Recruitment and Candidate Information</h2>
          <p className="policy-paragraph">
            If you apply for a position with Tellida or submit your CV to us, we may
            collect and process information including:
          </p>
          <ul className="policy-list">
            <li>Name and contact details;</li>
            <li>CV and employment history;</li>
            <li>Qualifications and professional experience;</li>
            <li>Education and training information;</li>
            <li>References;</li>
            <li>Information provided during interviews or assessments; and</li>
            <li>Other information reasonably required for recruitment.</li>
          </ul>

          <h3 className="policy-subheading">6.1 Recruitment Lawful Basis</h3>
          <p className="policy-paragraph">
            Recruitment information may be processed because it is necessary to take
            steps at your request before entering into a potential employment
            relationship, because Tellida has a legitimate interest in assessing
            candidates, or where otherwise permitted or required by applicable law.
          </p>
          <p className="policy-paragraph">
            Tellida will not treat submission of a CV as automatic or blanket consent
            to all processing described in this Privacy Policy. Where Tellida requires
            consent for a particular recruitment-related purpose, that consent will be
            requested separately and will not be assumed merely because a candidate
            submits a CV.
          </p>

          <h3 className="policy-subheading">6.2 Recruitment Data Retention</h3>
          <p className="policy-paragraph">
            Tellida generally retains recruitment CVs and related candidate
            information for a maximum period of six (6) months from the date of
            receipt, unless a longer period is required or permitted by applicable law
            or where the candidate has separately provided valid consent for continued
            consideration for future opportunities. The purpose of retaining this
            information is to:
          </p>
          <ul className="policy-list">
            <li>Assess candidates for current vacancies;</li>
            <li>Consider candidates for suitable future opportunities where permitted;</li>
            <li>Maintain recruitment records; and</li>
            <li>Meet applicable legal or administrative requirements.</li>
          </ul>
          <p className="policy-paragraph">
            After the applicable retention period, recruitment information will be
            securely deleted or anonymised, subject to any legal, regulatory or
            operational requirement requiring continued retention. If you wish to
            request deletion of your CV before the end of the applicable retention
            period, you may contact us using the details provided in this Privacy
            Policy.
          </p>
        </div>

        <div className="policy-section" id="employee-data">
          <h2 className="policy-heading">7. Employee Personal Data</h2>
          <p className="policy-paragraph">
            Where you become an employee or worker of Tellida, we may process personal
            data necessary for managing your employment relationship and meeting our
            legal, contractual and administrative responsibilities. This may include:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Personal identification information</li>
            <li>Contact and emergency contact details</li>
            <li>Employment history</li>
            <li>Attendance and working-time information</li>
            <li>Performance and training information</li>
            <li>Payroll and banking information</li>
            <li>Statutory information</li>
            <li>Benefits information</li>
            <li>Leave information</li>
            <li>Company identification information</li>
            <li>HR records</li>
            <li>Other information reasonably required for employment administration</li>
          </ul>
          <p className="policy-paragraph">
            Employee information is accessed only by authorised personnel who have a
            legitimate business need to access it. Where special categories of
            personal data are processed, Tellida will apply any additional safeguards
            and legal requirements applicable to such processing.
          </p>
        </div>

        <div className="policy-section" id="employee-file-retention">
          <h2 className="policy-heading">8. Employee Personal File Retention</h2>
          <p className="policy-paragraph">
            Tellida may retain employee personal files after an employee's resignation
            or termination where necessary for:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Legal or regulatory compliance</li>
            <li>Employment verification</li>
            <li>Statutory requirements</li>
            <li>Payroll and employment records</li>
            <li>Responding to legitimate requests from government or regulatory authorities</li>
            <li>Defending or establishing legal claims</li>
            <li>Internal audit and recordkeeping</li>
            <li>Other legitimate administrative purposes</li>
          </ul>
          <p className="policy-paragraph">
            Tellida may need to retain certain employment information because
            government or statutory bodies may require former employers to verify
            employment history or provide supporting information. Employee records
            will be retained only for as long as reasonably necessary for the relevant
            purpose or as required or permitted by applicable law. Records will be
            securely stored and access will be restricted to authorised personnel in
            accordance with Tellida's information security controls and ISMS
            requirements.
          </p>
        </div>

        <div className="policy-section" id="employee-media">
          <h2 className="policy-heading">9. Employee Photographs, Videos and Marketing Information</h2>
          <p className="policy-paragraph">
            Tellida may use limited employee information for corporate communications
            and marketing activities. This may include:
          </p>
          <ul className="policy-list">
            <li>Photographs;</li>
            <li>Group photographs;</li>
            <li>Video recordings from work-related activities or company events;</li>
            <li>Employee name and surname;</li>
            <li>Job title; and</li>
            <li>Professional designation.</li>
          </ul>
          <p className="policy-paragraph">This information may be used for:</p>
          <ul className="policy-list policy-list--grid">
            <li>Tellida's corporate website</li>
            <li>LinkedIn</li>
            <li>Approved social media channels</li>
            <li>Presentations</li>
            <li>Brochures</li>
            <li>Proposals</li>
            <li>Case studies</li>
            <li>Company announcements</li>
            <li>Event materials</li>
            <li>Other approved corporate communications</li>
          </ul>
          <p className="policy-paragraph">
            Where consent is required, Tellida will obtain consent separately and will
            not make the provision of such consent a condition of employment. Employees
            may withdraw consent by contacting HR at{" "}
            <a href="mailto:hr@tellida.com">hr@tellida.com</a>. Where consent is
            withdrawn, Tellida will take reasonable steps to stop future use of the
            relevant content. Withdrawal may not always permit removal from materials
            that have already been distributed, published, archived, or otherwise
            lawfully used before the withdrawal.
          </p>
        </div>

        <div className="policy-section" id="marketing">
          <h2 className="policy-heading">10. Marketing and Business Communications</h2>
          <p className="policy-paragraph">
            Tellida may process professional contact information for business
            development, relationship management and corporate communications. This
            may include:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Name</li>
            <li>Job title</li>
            <li>Company</li>
            <li>Business email address</li>
            <li>Business telephone number</li>
            <li>Professional interests</li>
            <li>Business relationship information</li>
            <li>Communications or interactions with Tellida</li>
          </ul>
          <p className="policy-paragraph">
            Depending on the circumstances and applicable law, Tellida may rely on
            legitimate interests or consent for business development and marketing
            activities. Where consent is required, Tellida will obtain consent through
            a clear affirmative action. Consent for marketing will not be bundled with
            consent for unrelated processing activities.
          </p>
          <p className="policy-paragraph">
            Where Tellida relies on legitimate interests for business communications,
            it will consider the individual's reasonable expectations, the nature of
            the communication and the impact of the processing. Where required,
            marketing communications will include an appropriate method to opt out or
            unsubscribe.
          </p>
          <p className="policy-paragraph">
            You may request that we stop sending marketing communications at any time.
            An objection to direct marketing will be respected and Tellida will stop
            processing personal data for direct marketing purposes, except where
            continued processing is permitted or required by applicable law for
            another legitimate purpose.
          </p>
        </div>

        <div className="policy-section" id="website-forms">
          <h2 className="policy-heading">11. Website Forms and Enquiries</h2>
          <p className="policy-paragraph">
            If you submit information through a Tellida website form, we may use the
            information provided to:
          </p>
          <ul className="policy-list">
            <li>Respond to your enquiry;</li>
            <li>Contact you regarding the services requested;</li>
            <li>Provide information about our services;</li>
            <li>Maintain a record of your enquiry; and</li>
            <li>Follow up on a business opportunity where appropriate.</li>
          </ul>
          <p className="policy-paragraph">
            The primary lawful basis for responding to a website enquiry will
            generally be legitimate interests and/or taking steps at the individual's
            request before entering into a potential contractual relationship,
            depending on the circumstances and applicable law.
          </p>
          <p className="policy-paragraph">
            Submitting a website enquiry does not constitute blanket consent to
            receive marketing communications. Where Tellida wishes to use your
            information for a separate purpose requiring consent, Tellida will request
            consent separately. We do not sell personal data submitted through our
            website.
          </p>
        </div>

        <div className="policy-section" id="cookies">
          <h2 className="policy-heading">12. Cookies and Similar Technologies</h2>
          <p className="policy-paragraph">
            Tellida may use cookies and similar technologies to operate, secure and
            improve our website. Cookies may be used for purposes such as:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Essential website functionality</li>
            <li>Website security</li>
            <li>Remembering preferences</li>
            <li>Understanding website usage</li>
            <li>Website performance and analytics</li>
            <li>Improving user experience</li>
          </ul>
          <p className="policy-paragraph">
            Where applicable, non-essential cookies will be used in accordance with
            applicable consent requirements. Where consent is required, Tellida will
            provide an appropriate mechanism allowing users to accept or reject
            different categories of non-essential cookies rather than relying on
            passive or pre-checked consent.
          </p>
          <p className="policy-paragraph">
            You can manage or disable cookies through your browser settings. Disabling
            certain cookies may affect website functionality. Where third-party
            analytics or tracking technologies are used, their processing may also be
            subject to the privacy policies of the relevant providers.
          </p>
        </div>

        <div className="policy-section" id="security">
          <h2 className="policy-heading">13. Information Security</h2>
          <p className="policy-paragraph">
            Tellida takes the security of personal data seriously. We maintain
            technical and organisational measures designed to protect personal data
            against:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Unauthorised access</li>
            <li>Accidental loss</li>
            <li>Unauthorised alteration</li>
            <li>Disclosure</li>
            <li>Destruction</li>
            <li>Misuse</li>
            <li>Other unlawful or unauthorised processing</li>
          </ul>
          <p className="policy-paragraph">Our information-security controls include, where appropriate:</p>
          <ul className="policy-list policy-list--grid">
            <li>Access controls</li>
            <li>Authentication controls</li>
            <li>Restricted access based on business need</li>
            <li>Secure information storage</li>
            <li>Security monitoring</li>
            <li>Backup and recovery measures</li>
            <li>Employee confidentiality obligations</li>
            <li>Security awareness and training</li>
            <li>Incident management procedures</li>
            <li>Regular reviews and audits</li>
            <li>Controls maintained under our Information Security Management System</li>
          </ul>
          <p className="policy-paragraph">
            Tellida's ISMS is aligned with ISO/IEC&nbsp;27001:2022. No method of
            transmission or electronic storage can be guaranteed to be completely
            secure. However, we continually review and improve our security controls
            to reduce information-security risks.
          </p>
        </div>

        <div className="policy-section" id="who-we-share">
          <h2 className="policy-heading">14. Who We Share Personal Data With</h2>
          <p className="policy-paragraph">
            We do not sell personal data. Where necessary and lawful, we may share
            personal data with:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Tellida or affiliated entities</li>
            <li>Employees and authorised personnel</li>
            <li>Clients and prospective clients where necessary for the relevant business relationship</li>
            <li>Professional advisers</li>
            <li>IT and technology service providers</li>
            <li>Cloud and hosting providers</li>
            <li>HR and recruitment service providers</li>
            <li>Payroll and financial service providers</li>
            <li>Marketing and communications service providers</li>
            <li>Website and analytics providers</li>
            <li>Security and infrastructure providers</li>
            <li>Professional service providers</li>
            <li>Government departments and regulatory authorities</li>
            <li>Law enforcement authorities where legally required</li>
            <li>Courts and tribunals</li>
            <li>Banks or financial institutions where necessary and authorised</li>
            <li>Transport or other operational service providers where necessary</li>
            <li>Other parties where disclosure is required or permitted by applicable law</li>
          </ul>
          <p className="policy-paragraph">
            Third-party service providers that process personal data on our behalf are
            expected to maintain appropriate confidentiality, security and data
            protection arrangements.
          </p>
        </div>

        <div className="policy-section" id="subprocessors">
          <h2 className="policy-heading">15. Third-Party Service Providers and Subprocessors</h2>
          <p className="policy-paragraph">
            Tellida may appoint third-party service providers and subprocessors to
            support the operation of our business and the delivery of our services.
            These may include providers of:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Website hosting</li>
            <li>Cloud infrastructure</li>
            <li>Email and communications</li>
            <li>Information technology</li>
            <li>Cybersecurity and infrastructure</li>
            <li>Recruitment and HR services</li>
            <li>Payroll and financial services</li>
            <li>Website analytics</li>
            <li>Marketing and communications</li>
            <li>Professional services</li>
            <li>Other business-support functions</li>
          </ul>
          <p className="policy-paragraph">
            Where a third party processes personal data on Tellida's behalf, Tellida
            will take reasonable steps to ensure that the provider is subject to
            appropriate contractual confidentiality, security and data protection
            obligations. Where applicable law or contractual requirements require
            Tellida to provide notice of a new or replacement subprocessor, Tellida
            will provide such notice through an appropriate communication channel
            before the relevant subprocessor is engaged.
          </p>
          <p className="policy-paragraph">
            Where an applicable law or contract provides a right to object to the
            appointment or replacement of a subprocessor, Tellida will provide the
            applicable individual or client with the opportunity to exercise that
            right in accordance with the relevant legal or contractual requirements.
            A request for information about the categories of third-party processors
            used by Tellida may be made using the contact details provided in this
            Privacy Policy.
          </p>
        </div>

        <div className="policy-section" id="client-data">
          <h2 className="policy-heading">16. Client Data and Outsourced Services</h2>
          <p className="policy-paragraph">
            Tellida provides outsourced business services to organisations that may
            involve processing personal data on behalf of those organisations. This
            may include data relating to:
          </p>
          <ul className="policy-list">
            <li>Their customers;</li>
            <li>Employees;</li>
            <li>Clients;</li>
            <li>Suppliers;</li>
            <li>Business contacts; and</li>
            <li>Other individuals connected with the client's operations.</li>
          </ul>
          <p className="policy-paragraph">
            Where Tellida processes personal data on behalf of a client, the client
            may remain the data controller and Tellida may act as a data processor. In
            these circumstances:
          </p>
          <ul className="policy-list">
            <li>We process personal data only for authorised business purposes;</li>
            <li>We follow applicable contractual instructions;</li>
            <li>We apply appropriate security controls;</li>
            <li>Access is restricted to authorised personnel;</li>
            <li>We maintain confidentiality requirements;</li>
            <li>We process data only for the purposes authorised by the client and applicable contract; and</li>
            <li>We support the client in meeting applicable data protection and security obligations where required by contract and law.</li>
          </ul>
          <p className="policy-paragraph">
            The client's own privacy notice may also apply to the processing of
            personal data within the relevant outsourced service.
          </p>
        </div>

        <div className="policy-section" id="international-transfers">
          <h2 className="policy-heading">17. International Data Transfers</h2>
          <p className="policy-paragraph">
            Tellida operates across Sri Lanka and the United Kingdom and may work with
            clients across the Globe, suppliers and technology providers located in
            other countries. Personal data may therefore be accessed, stored or
            processed across national borders where necessary for legitimate business
            operations or service delivery. This may include transfers or access
            between:
          </p>
          <ul className="policy-list">
            <li>Sri Lanka and the United Kingdom;</li>
            <li>The United Kingdom and Sri Lanka; and</li>
            <li>Other countries in which Tellida's clients, suppliers, technology providers or other authorised service providers operate.</li>
          </ul>
          <p className="policy-paragraph">
            Where personal data originating in the United Kingdom is transferred to or
            accessed from Sri Lanka, Tellida will use the transfer mechanism and
            safeguards required by applicable UK data protection law and the relevant
            contractual arrangements. Depending on the circumstances, such safeguards
            may include an applicable UK International Data Transfer Agreement (IDTA),
            UK Addendum to approved Standard Contractual Clauses, or another legally
            recognised transfer mechanism, together with appropriate technical and
            organisational security measures.
          </p>
          <p className="policy-paragraph">
            Where personal data is transferred to another jurisdiction, Tellida will
            assess and apply the safeguards required by the applicable data protection
            legislation and contractual arrangements. Such safeguards may include:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Appropriate contractual protections</li>
            <li>Data processing agreements</li>
            <li>International transfer agreements or recognised transfer mechanisms</li>
            <li>Confidentiality obligations</li>
            <li>Access controls</li>
            <li>Encryption and other security measures</li>
            <li>Data minimisation</li>
            <li>Restrictions on onward transfers</li>
            <li>Other safeguards required by applicable law</li>
          </ul>
          <p className="policy-paragraph">
            Where a client remains the data controller, the relevant international
            transfer arrangements may also be governed by the client's contractual
            instructions and data-processing agreement with Tellida. Individuals may
            contact Tellida using the details in this Privacy Policy to request
            further information about the applicable international transfer
            safeguards relating to their personal data.
          </p>
        </div>

        <div className="policy-section" id="data-retention">
          <h2 className="policy-heading">18. Data Retention</h2>
          <p className="policy-paragraph">
            Tellida retains personal data only for as long as reasonably necessary for
            the specific purpose for which it was collected, unless a longer period is
            required or permitted by law. Retention periods are determined by
            considering:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>The purpose for which the information was collected</li>
            <li>The nature of the relationship with Tellida</li>
            <li>Contractual requirements</li>
            <li>Legal and regulatory obligations</li>
            <li>Accounting and financial requirements</li>
            <li>Employment requirements</li>
            <li>Dispute resolution requirements</li>
            <li>Information-security requirements</li>
            <li>Applicable limitation periods</li>
            <li>The need to establish, exercise or defend legal claims</li>
          </ul>
          <p className="policy-paragraph">
            Tellida applies retention periods to specific categories of personal data
            rather than retaining all information indefinitely.
          </p>

          <h3 className="policy-subheading">Indicative Retention Schedule</h3>
          <div className="policy-table-wrap">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Typical Retention Period</th>
                  <th>Purpose / Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Website contact and enquiry information</td>
                  <td>As long as reasonably necessary to respond to and manage the enquiry and related business relationship</td>
                  <td>Responding to enquiries, relationship management and business administration</td>
                </tr>
                <tr>
                  <td>Prospective-client/business-development information</td>
                  <td>As long as reasonably necessary for the relevant relationship or business-development purpose, subject to applicable objection and deletion rights</td>
                  <td>Business development and relationship management</td>
                </tr>
                <tr>
                  <td>Marketing contact information</td>
                  <td>Until the individual unsubscribes/objects or the information is no longer reasonably necessary for the marketing purpose, subject to applicable law</td>
                  <td>Marketing and communications</td>
                </tr>
                <tr>
                  <td>Recruitment CVs and candidate information</td>
                  <td>Generally up to six (6) months from receipt unless a longer period is legally permitted, required or separately consented to where applicable</td>
                  <td>Recruitment and candidate assessment</td>
                </tr>
                <tr>
                  <td>Employee personal records</td>
                  <td>For the duration of employment and thereafter for the period reasonably necessary to meet legal, statutory, contractual, employment, audit and legitimate business requirements</td>
                  <td>Employment administration and legal compliance</td>
                </tr>
                <tr>
                  <td>Payroll, accounting and financial records</td>
                  <td>For the period required by applicable law, accounting requirements, contractual obligations and legitimate business needs</td>
                  <td>Financial and statutory administration</td>
                </tr>
                <tr>
                  <td>Client and contractual records</td>
                  <td>For the duration of the relationship and thereafter for the period required by contract, applicable law, dispute-resolution requirements and legitimate business purposes</td>
                  <td>Contract management and legal protection</td>
                </tr>
                <tr>
                  <td>Website security and technical logs</td>
                  <td>For the period reasonably necessary for security, monitoring, incident investigation and legal requirements</td>
                  <td>Information security and system administration</td>
                </tr>
                <tr>
                  <td>Consent and preference records</td>
                  <td>For as long as reasonably necessary to demonstrate the individual's consent, withdrawal or communication preferences and to meet applicable legal requirements</td>
                  <td>Accountability and compliance</td>
                </tr>
                <tr>
                  <td>Personal data relating to legal claims or disputes</td>
                  <td>Until the relevant matter is resolved and for any additional period reasonably necessary for limitation, enforcement or legal requirements</td>
                  <td>Establishing, exercising or defending legal claims</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="policy-paragraph">
            The periods above are indicative and may vary depending on the nature of
            the information, the applicable law, contractual requirements and the
            circumstances of the processing. Where a specific statutory or contractual
            retention period applies, Tellida will follow that requirement. When
            personal data is no longer required, it will be securely deleted,
            anonymised or otherwise disposed of in accordance with our
            information-security and records-management procedures.
          </p>
        </div>

        <div className="policy-section" id="your-rights">
          <h2 className="policy-heading">19. Your Data Protection Rights</h2>
          <p className="policy-paragraph">
            Depending on the applicable law and circumstances of the processing, you
            may have rights including:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>The right to be informed about how your personal data is used</li>
            <li>The right to request access to personal data held about you</li>
            <li>The right to request correction of inaccurate or incomplete information</li>
            <li>The right to request deletion of personal data where applicable</li>
            <li>The right to request restriction of processing where applicable</li>
            <li>The right to object to certain processing</li>
            <li>The right to object to direct marketing</li>
            <li>The right to withdraw consent where processing is based on consent</li>
            <li>The right to data portability where applicable</li>
            <li>Rights relating to certain automated decision-making or profiling</li>
            <li>The right to lodge a complaint with the relevant data protection authority where applicable</li>
          </ul>
          <p className="policy-paragraph">
            These rights are subject to applicable legal conditions and exemptions and
            may not apply in every circumstance. For example, Tellida may be legally
            required to retain certain information even where an individual requests
            deletion.
          </p>
        </div>

        <div className="policy-section" id="exercise-rights">
          <h2 className="policy-heading">20. How to Exercise Your Data Protection Rights</h2>
          <p className="policy-paragraph">
            If you wish to exercise a data protection right or ask how Tellida
            processes your personal data, you may submit a request by email to:{" "}
            <a href="mailto:hr@tellida.com">hr@tellida.com</a>
          </p>
          <p className="policy-paragraph">
            For general website, business or privacy-related enquiries, you may also
            contact: <a href="mailto:info@tellida.com">info@tellida.com</a>
          </p>

          <h3 className="policy-subheading">20.1 Information to Include</h3>
          <p className="policy-paragraph">To help us process your request, please provide:</p>
          <ul className="policy-list">
            <li>Your name;</li>
            <li>Contact details;</li>
            <li>The nature of your request;</li>
            <li>A description of the personal data or processing activity concerned, where known; and</li>
            <li>Any other information reasonably necessary to identify the relevant records.</li>
          </ul>
          <p className="policy-paragraph">You do not need to use a particular form to exercise your rights.</p>

          <h3 className="policy-subheading">20.2 Identity Verification</h3>
          <p className="policy-paragraph">
            To protect personal data from unauthorised disclosure, Tellida may need to
            verify your identity before responding to a request. We will request only
            information reasonably necessary for identity verification and will
            handle verification information securely.
          </p>

          <h3 className="policy-subheading">20.3 Processing Your Request</h3>
          <p className="policy-paragraph">After receiving a request, Tellida will:</p>
          <ul className="policy-list">
            <li>Record and review the request;</li>
            <li>Determine which legal rights and requirements apply;</li>
            <li>Verify identity where reasonably necessary;</li>
            <li>Locate and review relevant personal data;</li>
            <li>Assess whether any legal exemptions or restrictions apply;</li>
            <li>Take the appropriate action; and</li>
            <li>Communicate the outcome to the requester.</li>
          </ul>
          <p className="policy-paragraph">
            We will respond to valid requests within the timeframe required by
            applicable law. Where an extension is legally permitted and reasonably
            necessary because of the complexity or number of requests, Tellida will
            communicate this in accordance with applicable law. If Tellida is unable
            to comply with a request, we will explain the reason where required or
            permitted by law.
          </p>
        </div>

        <div className="policy-section" id="withdrawal-of-consent">
          <h2 className="policy-heading">21. Withdrawal of Consent</h2>
          <p className="policy-paragraph">
            Where Tellida processes your personal data based on consent, you may
            withdraw that consent at any time. Consent will be requested for a
            specific purpose and will not be assumed merely because you provide
            personal data to Tellida. For example:
          </p>
          <ul className="policy-list">
            <li>Submitting a CV does not automatically constitute consent to receive marketing communications;</li>
            <li>Submitting a website enquiry does not automatically constitute consent to receive unrelated marketing communications; and</li>
            <li>Providing personal data for one purpose does not automatically constitute consent to unrelated processing activities.</li>
          </ul>
          <p className="policy-paragraph">
            Withdrawal of consent does not affect the lawfulness of processing that
            took place before the withdrawal. You may withdraw consent by contacting{" "}
            <a href="mailto:hr@tellida.com">hr@tellida.com</a>. For marketing
            communications, you may also use the unsubscribe mechanism provided in the
            relevant communication.
          </p>
          <p className="policy-paragraph">
            Where consent is withdrawn, Tellida will stop the relevant consent-based
            processing unless another lawful basis permits or requires continued
            processing.
          </p>
        </div>

        <div className="policy-section" id="complaints">
          <h2 className="policy-heading">22. Complaints</h2>
          <p className="policy-paragraph">
            If you have concerns about how Tellida handles your personal data, we
            encourage you to contact us first so that we can investigate and address
            your concern. You may submit a privacy complaint to:{" "}
            <a href="mailto:info@tellida.com">info@tellida.com</a>
          </p>
          <p className="policy-paragraph">
            Tellida will review and investigate privacy complaints and will take
            appropriate action where necessary. You may also have the right to lodge a
            complaint with the relevant data protection or supervisory authority
            depending on your location and the law applicable to the processing.
          </p>
          <p className="policy-paragraph">
            For individuals in the United Kingdom, the relevant supervisory authority
            is generally the Information Commissioner's Office (ICO). For matters
            governed by Sri Lankan data protection legislation, the relevant authority
            may be the Data Protection Authority of Sri Lanka.
          </p>
        </div>

        <div className="policy-section" id="childrens-privacy">
          <h2 className="policy-heading">23. Children's Privacy</h2>
          <p className="policy-paragraph">
            Tellida's website and business services are not generally directed at
            children. We do not knowingly collect personal data from children through
            our website unless such collection is necessary and permitted for a
            specific legitimate purpose. If you believe that a child has provided
            personal data to us without appropriate authorisation, please contact us
            so that we can review and take appropriate action.
          </p>
        </div>

        <div className="policy-section" id="special-categories">
          <h2 className="policy-heading">24. Special Categories of Personal Data</h2>
          <p className="policy-paragraph">
            Tellida does not generally seek to collect special categories of personal
            data through its public website. Where special category or sensitive
            personal data is required for employment, legal, regulatory or contractual
            purposes, Tellida will process such information only where permitted by
            applicable law and with appropriate safeguards. Where applicable, Tellida
            will identify and apply the additional legal condition required for
            processing special category personal data.
          </p>
        </div>

        <div className="policy-section" id="automated-decisions">
          <h2 className="policy-heading">25. Automated Decision-Making and Profiling</h2>
          <p className="policy-paragraph">
            Tellida does not generally make decisions producing legal or similarly
            significant effects on individuals solely through automated
            decision-making. Where automated processing or profiling is introduced in
            a manner that creates applicable legal obligations or individual rights,
            Tellida will provide the information and safeguards required by applicable
            law.
          </p>
        </div>

        <div className="policy-section" id="third-party-sites">
          <h2 className="policy-heading">26. Third-Party Websites</h2>
          <p className="policy-paragraph">
            Our website may contain links to third-party websites, platforms or
            services. Tellida is not responsible for the privacy practices, security
            or content of third-party websites. We recommend reviewing the privacy
            notice of any third-party website before providing personal data.
          </p>
        </div>

        <div className="policy-section" id="data-breaches">
          <h2 className="policy-heading">27. Data Breaches and Security Incidents</h2>
          <p className="policy-paragraph">
            Tellida maintains procedures for identifying, assessing, managing and
            responding to information-security incidents and personal data breaches.
            Where a personal data breach occurs, Tellida will:
          </p>
          <ul className="policy-list">
            <li>Identify and contain the incident;</li>
            <li>Assess the nature and scope of the breach;</li>
            <li>Determine the categories of personal data and individuals affected;</li>
            <li>Assess the potential risks to affected individuals;</li>
            <li>Take appropriate measures to mitigate the effects of the breach;</li>
            <li>Document the incident and response actions; and</li>
            <li>Determine whether notification to clients, individuals, regulators or other parties is legally or contractually required.</li>
          </ul>
          <p className="policy-paragraph">
            Where notification to a relevant supervisory authority is legally
            required, Tellida will make the notification within the applicable
            statutory timeframe. Where notification to affected individuals is legally
            required, Tellida will notify them within the applicable timeframe and in
            the manner required by applicable law.
          </p>
          <p className="policy-paragraph">
            Where Tellida processes personal data on behalf of a client, Tellida will
            notify and cooperate with the relevant client in accordance with the
            applicable data-processing agreement and contractual requirements. Where
            notification is not legally required, Tellida may nevertheless take
            appropriate communication measures where necessary to protect individuals
            or meet contractual or regulatory obligations.
          </p>
        </div>

        <div className="policy-section" id="confidentiality">
          <h2 className="policy-heading">28. Confidentiality</h2>
          <p className="policy-paragraph">
            Tellida requires employees, contractors and relevant third parties who
            have authorised access to personal data to maintain appropriate
            confidentiality and security. Access to personal data is limited according
            to business requirements and authorised responsibilities. Employees and
            relevant contractors receive appropriate information-security and privacy
            awareness training based on their roles and responsibilities.
          </p>
        </div>

        <div className="policy-section" id="changes">
          <h2 className="policy-heading">29. Changes to This Privacy Policy</h2>
          <p className="policy-paragraph">
            Tellida may update this Privacy Policy from time to time to reflect:
          </p>
          <ul className="policy-list policy-list--grid">
            <li>Changes in legislation or regulatory requirements</li>
            <li>Changes to our services</li>
            <li>Changes to our technology or website</li>
            <li>Changes to our data-processing activities</li>
            <li>Changes to our third-party service providers or subprocessors</li>
            <li>Improvements to our privacy and information-security practices</li>
          </ul>
          <p className="policy-paragraph">
            The latest version will be published on the Tellida website. Where
            appropriate, significant changes will be communicated through suitable
            channels. Where applicable law or contractual requirements require prior
            notification of material changes affecting the processing of personal
            data, Tellida will provide such notification.
          </p>
        </div>

        <div className="policy-section" id="contact">
          <h2 className="policy-heading">30. Contact Us</h2>
          <p className="policy-paragraph">
            If you have questions about this Privacy Policy, our processing of
            personal data, your data protection rights, or the safeguards applied to
            international transfers, please contact us.
          </p>
          <p className="policy-paragraph">
            <strong>Tellida (Pvt) Ltd</strong>
            <br />
            Level 05, No. 228, Galle Road, Colombo 04, Sri Lanka
            <br />
            General Enquiries: <a href="mailto:info@tellida.com">info@tellida.com</a>
            <br />
            HR / Personal Data Requests: <a href="mailto:hr@tellida.com">hr@tellida.com</a>
          </p>
        </div>

        <div className="policy-summary">
          <h2 className="policy-summary-title">Privacy Policy Summary</h2>
          <p className="policy-paragraph">
            Tellida is committed to five core principles when handling personal data:
          </p>
          <ul className="policy-list">
            <li>Collect only what we reasonably need.</li>
            <li>Use personal data for specific and clearly communicated purposes.</li>
            <li>Identify and apply the appropriate lawful basis for processing.</li>
            <li>Keep personal data secure and retain it only for as long as necessary.</li>
            <li>Respect and facilitate the rights of individuals over their personal data.</li>
          </ul>
          <p className="policy-paragraph">
            This Privacy Policy should be read together with any applicable client
            contracts, employment policies, recruitment notices, cookie notices,
            consent mechanisms, data-processing agreements and other privacy
            information provided by Tellida where additional information is required
            for a particular processing activity.
          </p>
          <p className="policy-paragraph">
            Where consent is required, Tellida will obtain it through a clear,
            specific and affirmative mechanism. Consent will not be assumed merely
            because an individual submits personal information to Tellida.
          </p>
          <p className="policy-paragraph">
            Where Tellida acts as a data processor on behalf of a client, the relevant
            client's privacy notice, contractual instructions and data-processing
            arrangements may also apply.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Policy;
